/* ==========================================================================
   FILE: services/brain.service.js
   IAMONEAI - Brain Service
   Settings, guardrails, personality
   ========================================================================== */

import { client } from '../api';

// ==========================================================================
// CACHE SETTINGS
// ==========================================================================

const CACHE_TTL = {
  brain: 300000, // 5 minutes
};

// ==========================================================================
// BRAIN SERVICE
// ==========================================================================

const brainService = {
  /**
   * Get user's brain (settings, guardrails, personality)
   * @param {string} iin 
   * @returns {Promise<Object>} Brain data
   */
  async get(iin) {
    return client.get(`/brain/${iin}`, {
      cache: true,
      cacheTTL: CACHE_TTL.brain,
    });
  },

  /**
   * Update brain settings
   * @param {string} iin 
   * @param {Object} updates - Partial brain updates
   * @returns {Promise<Object>}
   */
  async update(iin, updates) {
    return client.put(`/brain/${iin}`, { updates }, {
      cache: false,
      priority: 'high',
    });
  },

  /**
   * Update guardrails only
   * @param {string} iin 
   * @param {Object} guardrails 
   * @returns {Promise<Object>}
   */
  async updateGuardrails(iin, guardrails) {
    return this.update(iin, { guardrails });
  },

  /**
   * Update personality only
   * @param {string} iin 
   * @param {Object} personality 
   * @returns {Promise<Object>}
   */
  async updatePersonality(iin, personality) {
    return this.update(iin, { personality });
  },

  /**
   * Update active context
   * @param {string} iin 
   * @param {string} context - personal/work/family
   * @returns {Promise<Object>}
   */
  async setActiveContext(iin, context) {
    return this.update(iin, { activeContext: context });
  },

  /**
   * Get guardrails only (extracted from brain)
   * @param {string} iin 
   * @returns {Promise<Object>}
   */
  async getGuardrails(iin) {
    const response = await this.get(iin);
    return response.brain?.guardrails || {};
  },

  /**
   * Get personality only (extracted from brain)
   * @param {string} iin 
   * @returns {Promise<Object>}
   */
  async getPersonality(iin) {
    const response = await this.get(iin);
    return response.brain?.personality || {};
  },

  /**
   * Get available contexts
   * @param {string} iin 
   * @returns {Promise<Object>}
   */
  async getContexts(iin) {
    const response = await this.get(iin);
    return response.brain?.contexts || {
      personal: { name: 'Personal', icon: '🏠', enabled: true },
      work: { name: 'Work', icon: '💼', enabled: true },
      family: { name: 'Family', icon: '👨‍👩‍👧‍👦', enabled: true },
    };
  },
};

// ==========================================================================
// EXPORTS
// ==========================================================================

export { brainService };
export default brainService;
